import { useMemo, useRef } from "react";
import { GridOptions, RowHighlightPosition } from "ag-grid-community";
import {
  ComplianceCaseTypeEnum,
  ProfileRelationshipEnum,
  ScreeningRecordResult,
} from "~/graphql/supergraphEnums";
import { useIsProfileDirtyQuery } from "~/graphql/supergraphHooks";
import { AccountTypeEnum as AccountType } from "~/graphql/supergraphTypes";
import { gridCellStyles } from "~/pages/Compliance/Common/GridCellBackgrounds/styles_vanilla.css";
import {
  useComplianceProfileRefreshTab,
  useIOReusableProfiles,
} from "~/pages/Compliance/flags";
import { getRiskRating, sortRiskRating } from "~/pages/Compliance/riskRating/utils";
import { isStandaloneCaseType } from "~/pages/Compliance/utils";
import { getScreeningCellStyle } from "../../PaymentInstructions/BeneficiarySummary/ScreeningCellRenderer";
import { showsPepExposure } from "../../pepExposure/constants";
import { getProfilePepExposure } from "../../pepExposure/utils";
import {
  ComplianceCaseMetaData,
  OwnersAndControllersRowItem,
  ProfileType,
  ProposedChange,
  UseProfileTreeDataParams,
  UseProfileTreeDataReturn,
} from "../types";
import {
  getCountryName,
  getInvestingProfileName,
  getIsPaymentBeneficiary,
  getOwnershipPercentageStr,
  getProfileNameForProfileType,
  getRelationships,
} from "../utils";
import { ProposedChangeCellRenderer } from "./ProposedChangeCellRenderer";

export const useProfileTreeData = ({
  complianceCase,
  caseId,
  programId,
  showScreeningColumns,
  enableRowDrag = false,
}: UseProfileTreeDataParams): UseProfileTreeDataReturn => {
  const annualRefreshEnabled = useComplianceProfileRefreshTab();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const investorAccount = getInvestingProfileName(complianceCase?.investingProfile);

  const isStandaloneCase = isStandaloneCaseType(
    complianceCase?.caseType as ComplianceCaseTypeEnum,
  );

  const isOnboarding =
    complianceCase?.caseType === ComplianceCaseTypeEnum.InvestorOnboarding ||
    complianceCase?.caseType === ComplianceCaseTypeEnum.Remediation;

  const extractChildProfiles = (): ProfileType[] => {
    if (!complianceCase?.profiles) return [];

    return complianceCase.profiles.filter((profile): profile is NonNullable<typeof profile> => {
      if (!profile) return false;

      const hasValidRelationships =
        profile.parentRelationships
          ?.map((pr) => pr?.relationshipType)
          .filter(
            (rt): rt is NonNullable<typeof rt> =>
              rt !== null &&
              rt !== undefined &&
              [ProfileRelationshipEnum.Owner, ProfileRelationshipEnum.Controller].includes(
                rt as ProfileRelationshipEnum,
              ),
          ).length > 0;

      return hasValidRelationships;
    });
  };

  const allChildProfiles = extractChildProfiles();

  const allProfileIds = [
    complianceCase?.investingProfile?.id ?? 0,
    ...allChildProfiles.map((profile) => profile?.id ?? 0),
  ];

  const isProfileDirtyResult = useIsProfileDirtyQuery({
    variables: {
      profileIds: allProfileIds,
    },
    skip: !annualRefreshEnabled || allProfileIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const profileDirtyStateMap = useMemo(() => {
    const list = isProfileDirtyResult.data?.isProfileDirty ?? [];

    return list.reduce(
      (acc, profile) => {
        acc[profile.profileId] = {
          isDirty: profile.isDirty,
          isDivergedFromLatest: profile.isDivergedFromLatest,
        };
        return acc;
      },
      {} as Record<string, { isDirty: boolean; isDivergedFromLatest: boolean } | undefined>,
    );
  }, [isProfileDirtyResult.data?.isProfileDirty]);

  const reviewedProposedChanges = useMemo(
    () =>
      (complianceCase?.metaData as ComplianceCaseMetaData | null | undefined)
        ?.reviewed_proposed_changes ?? {},
    [complianceCase?.metaData],
  );

  const mergedRowData: OwnersAndControllersRowItem[] = [];

  const proposedChangeInfo = (
    sourceProfileId: number | null,
    profileId: number,
    latestVersionId: number | null,
  ): ProposedChange | undefined => {
    const showLatestVersion =
      ioReusableProfilesEnabled &&
      latestVersionId != null &&
      sourceProfileId != null &&
      latestVersionId > sourceProfileId;
    const dirtyState = profileDirtyStateMap?.[profileId];
    const isDirty = !!sourceProfileId && !!dirtyState?.isDirty;
    const isDivergedFromLatest =
      ioReusableProfilesEnabled && !!sourceProfileId && !!dirtyState?.isDivergedFromLatest;

    if (!isDirty && !isDivergedFromLatest) return undefined;

    const reviewedProfileId =
      reviewedProposedChanges[String(profileId)]?.reviewed_profile_id ?? null;

    return {
      sourceProfileId,
      targetProfileId: profileId,
      latestVersionId: showLatestVersion ? latestVersionId : null,
      reviewedProfileId,
      caseId,
      programId: programId ?? null,
    };
  };

  const investingProfileId = complianceCase?.investingProfile?.id;

  const isRecurringHitCase = complianceCase?.caseType === ComplianceCaseTypeEnum.RecurringHit;
  const rootRelationshipLabel = getRelationships(
    complianceCase?.investingProfile?.parentRelationships,
  );
  const investorRootRelationship =
    isRecurringHitCase && rootRelationshipLabel !== "--"
      ? rootRelationshipLabel
      : "Investor Account";

  if (investorAccount && investingProfileId) {
    mergedRowData.push({
      id: String(investingProfileId),
      parentId: null,
      name: investorAccount,
      relationship: investorRootRelationship,
      ownership: "--",
      domicile: getCountryName(complianceCase?.investingProfile?.addresses),
      riskRating: getRiskRating(complianceCase?.investingProfile?.riskRating, programId),
      pepExposure: getProfilePepExposure(complianceCase?.investingProfile),
      // json scalar type is unknown, cast to any as we don't validate the structure
      screeningRecord: (complianceCase?.investingProfile?.screeningRecord as any) || null,
      screening: {
        caseId: caseId,
        profileId: investingProfileId,
        screeningRecord: complianceCase?.investingProfile?.screeningRecord?.result || null,
      },
      proposedChange: proposedChangeInfo(
        complianceCase?.investingProfile?.sourceProfileId || null,
        investingProfileId,
        complianceCase?.investingProfile?.latestVersion?.id ?? null,
      ),
      path: [String(investingProfileId)],
      isInvestorRoot: true,
      accountType: complianceCase?.investingProfile?.accountType as AccountType | null,
      complianceObjectType: complianceCase?.investingProfile?.complianceObjectType ?? null,
      actions: {
        id: investingProfileId,
        name: investorAccount,
        isPaymentBeneficiary: false,
      },
    });
  }

  const childRows: OwnersAndControllersRowItem[] = allChildProfiles.map((profile) => {
    const profileId = String(profile?.id);
    const profilePath = profile?.path || "";
    const pathIds = profilePath.split("/").filter((x) => x.length > 0);
    const profileName = getProfileNameForProfileType(profile);

    // Build path: [rootId, ...ancestorIds, thisProfileId]
    // Ensure path always ends with this profile's ID
    const ancestorIds = pathIds.filter((id) => id !== profileId);
    const idPath = investingProfileId
      ? [String(investingProfileId), ...ancestorIds, profileId]
      : [...ancestorIds, profileId];

    // Parent is the second-to-last element in the path
    const parentId = idPath.length > 1 ? idPath[idPath.length - 2] : null;

    return {
      id: profileId,
      parentId,
      name: profileName,
      accountType: profile?.accountType as AccountType | null,
      complianceObjectType: profile?.complianceObjectType ?? null,
      relationship: getRelationships(profile?.parentRelationships),
      ownership: getOwnershipPercentageStr(profile?.parentRelationships),
      domicile: getCountryName(profile?.addresses || []) || "",
      riskRating: getRiskRating(profile?.riskRating, programId),
      pepExposure: getProfilePepExposure(profile),
      screeningRecord: (profile?.screeningRecord as any) || null,
      screening: {
        caseId: caseId,
        profileId: profile?.id,
        screeningRecord: profile?.screeningRecord?.result || null,
      },
      proposedChange: proposedChangeInfo(
        profile?.sourceProfileId || null,
        profile?.id ?? 0,
        profile?.latestVersion?.id ?? null,
      ),
      path: idPath,
      actions: {
        id: profile?.id,
        name: profileName,
        isPaymentBeneficiary: getIsPaymentBeneficiary(
          profile?.id,
          profile?.paymentAccounts ?? [],
        ),
      },
    };
  });

  mergedRowData.push(...childRows);
  const rowData = mergedRowData;

  const screeningStatusColumn = {
    field: "screeningRecord",
    headerName: "Screening Status",
    sortable: false,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
  };

  const riskRatingColumn = {
    field: "riskRating",
    headerName: "Risk rating",
    comparator: sortRiskRating,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
  };

  const pepExposureColumn = {
    field: "pepExposure",
    headerName: "PEP exposure",
    sortable: false,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
  };

  const screeningResultColumn = {
    minWidth: 240,
    field: "screening",
    headerName: "Screening Result",
    sortable: false,
    cellStyle: {
      cursor: "pointer",
    },
    cellClass: (params: { data: OwnersAndControllersRowItem }) => {
      const rowData = params.data;
      const screeningResult = rowData?.screening
        ?.screeningRecord as ScreeningRecordResult | null;
      return getScreeningCellStyle(screeningResult);
    },
  };

  const screeningColumns = showScreeningColumns
    ? [screeningStatusColumn, screeningResultColumn]
    : [];

  const proposedChangeColumn = {
    minWidth: 150,
    field: "proposedChange",
    headerName: "Proposed Change",
    sortable: false,
    cellRenderer: ProposedChangeCellRenderer,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
    cellClass: (params: { data: OwnersAndControllersRowItem }) => {
      const proposedChange = params.data?.proposedChange;
      if (!proposedChange) return gridCellStyles.default;
      const { latestVersionId, reviewedProfileId } = proposedChange;
      const isDivergedFromReview =
        latestVersionId != null && latestVersionId !== reviewedProfileId;
      return isDivergedFromReview ? gridCellStyles.danger : gridCellStyles.information;
    },
  };

  const baseCols = [
    {
      field: "domicile",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
    ...(isStandaloneCase ? [] : [riskRatingColumn]),
    ...(showsPepExposure(complianceCase?.caseType) ? [pepExposureColumn] : []),
    ...screeningColumns,
    ...(isStandaloneCase || (ioReusableProfilesEnabled && isOnboarding)
      ? [proposedChangeColumn]
      : []),
    {
      maxWidth: 100,
      field: "actions",
      headerName: "",
      resizable: false,
      sortable: false,
    },
  ];

  const baseColumnDefs = [
    {
      field: "relationship",
      headerName: "Role",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
    {
      field: "ownership",
      headerName: "Ownership %",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
    ...baseCols,
  ];
  const currentDropTargetRef = useRef<string | null>(null);

  const highlightDropTarget = (api: unknown, nodeId: string | null) => {
    const gridApi = api as
      | {
          getRowNode: (
            id: string,
          ) => { setHighlighted: (value: RowHighlightPosition | null) => void } | undefined;
        }
      | undefined;
    if (!gridApi) return;

    const current = currentDropTargetRef.current;
    if (current) {
      gridApi.getRowNode(current)?.setHighlighted(null);
    }
    if (nodeId) {
      gridApi.getRowNode(nodeId)?.setHighlighted(RowHighlightPosition.Below);
    }
    currentDropTargetRef.current = nodeId;
  };

  const gridOptions: GridOptions<OwnersAndControllersRowItem> = {
    treeData: true,
    getDataPath: (row: OwnersAndControllersRowItem): string[] => row.path,
    getRowId: (params) => params.data.id,
    groupDefaultExpanded: -1,
    ...(enableRowDrag && {
      rowDragManaged: false,
      onRowDragMove: (event) => {
        const overNode = event.overNode;
        const draggedNode = event.node;
        if (overNode?.data && draggedNode?.data) {
          const isSelf = overNode.data.id === draggedNode.data.id;
          const isDescendant = overNode.data.path
            .join("/")
            .startsWith(draggedNode.data.path.join("/") + "/");
          if (!isSelf && !isDescendant) {
            highlightDropTarget(event.api, overNode.data.id);
          } else {
            highlightDropTarget(event.api, null);
          }
        }
      },
      onRowDragLeave: (event) => {
        highlightDropTarget(event.api, null);
      },
      onRowDragEnd: (event) => {
        highlightDropTarget(event.api, null);

        const draggedNode = event.node;
        const overNode = event.overNode;
        if (!draggedNode?.data || !overNode?.data) return;

        const draggedData = draggedNode.data;
        const targetData = overNode.data;

        // Prevent dropping on self or descendants
        if (draggedData.id === targetData.id) return;
        if (targetData.path.join("/").startsWith(draggedData.path.join("/") + "/")) return;

        // Calculate new path: target's path + dragged node's id
        const newPath = [...targetData.path, draggedData.id];
        // New parent is the drop target
        const newParentId = targetData.id;

        // Update dragged row and all its descendants
        const oldPathPrefix = draggedData.path.join("/");
        const updates: OwnersAndControllersRowItem[] = [];

        event.api.forEachNode((node) => {
          if (!node.data) return;
          const nodePath = node.data.path.join("/");

          if (node.data.id === draggedData.id) {
            // Update both path AND parentId for the dragged node
            updates.push({ ...node.data, path: newPath, parentId: newParentId });
          } else if (nodePath.startsWith(oldPathPrefix + "/")) {
            // Descendant - update path and recalculate parentId from new path
            const relativePath = node.data.path.slice(draggedData.path.length);
            const descendantNewPath = [...newPath, ...relativePath];
            const descendantParentId =
              descendantNewPath.length > 1
                ? descendantNewPath[descendantNewPath.length - 2]
                : null;
            updates.push({
              ...node.data,
              path: descendantNewPath,
              parentId: descendantParentId,
            });
          }
        });

        event.api.applyTransaction({ update: updates });

        // Auto-size the group column after hierarchy change
        setTimeout(() => {
          event.api.autoSizeColumns(["ag-Grid-AutoColumn"]);
        }, 0);
      },
    }),
    onFirstDataRendered: (event) => {
      event.api.autoSizeAllColumns();
    },
    autoGroupColumnDef: {
      minWidth: 200,
      headerName: "Name",
      field: "name",
      rowDrag: enableRowDrag ? (params) => !params.data?.isInvestorRoot : false,
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: "profileNameRenderer",
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
    domLayout: "autoHeight",
    rowHeight: 46,
  };

  return { rowData, baseColumnDefs: baseColumnDefs, gridOptions };
};
